import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import RegisterForm from '../components/Auth/RegisterForm';
import { Card, Button, Divider } from '../components/common';
import { BRAND_LOGO_FULL_SRC } from '../config/brandAssets';
import { GoogleIcon } from '../components/Icons';
import { authService } from '../services/authService';
import { UserRole } from '../types/firestore';
import { savePostAuthIntentFromQueryValue } from '../utils/postAuthIntent';

interface RegisterPageProps {
  role: UserRole;
  showGoogleAuth?: boolean;
}

const RegisterPage: React.FC<RegisterPageProps> = ({ role, showGoogleAuth = false }) => {
  const location = useLocation();
  const from = (location.state as any)?.from;
  const isServiceRegistration = role === UserRole.SERVICE_ADMIN;
  const queryParams = new URLSearchParams(location.search);
  const postAuthIntent = queryParams.get('postAuthIntent');

  useEffect(() => {
    if (!postAuthIntent) {
      return;
    }
    savePostAuthIntentFromQueryValue(postAuthIntent, 'register-page-query');
  }, [postAuthIntent]);

  const loginTarget = postAuthIntent ? `/login?postAuthIntent=${postAuthIntent}` : '/login';

  const handleGoogleRegister = async () => {
    try {
      await authService.signInWithGoogle();
    } catch (error: any) {
      console.error('Błąd rejestracji/logowania przez Google:', error);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-primary-dark-100">
      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-4 py-10">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-block group focus-outline rounded-md">
            <img
              src={BRAND_LOGO_FULL_SRC}
              alt="Ratuj Sprzęt Logo"
              className="mx-auto mb-2 h-14 w-14 transition-opacity group-hover:opacity-90"
            />
            <h1 className="text-2xl font-display font-bold text-primary-light-100 group-hover:text-primary-light-200 transition-colors">
              Ratuj Sprzęt
            </h1>
          </Link>
        </div>

        <Card className="mb-8 w-full max-w-md animate-slide-up p-6 sm:p-8">
          <h2 className="text-xl font-bold text-primary-light-100 mb-2 text-center">
            {isServiceRegistration ? 'Dołącz jako serwis' : 'Załóż konto klienta'}
          </h2>
          <p className="text-sm text-primary-light-300 text-center mb-6">
            {isServiceRegistration
              ? 'Utwórz konto serwisu i rozpocznij proces weryfikacji.'
              : 'Załóż konto klienta, aby składać zgłoszenia naprawy.'}
          </p>

          {showGoogleAuth && (
            <>
              <div className="mb-6">
                <Button
                  variant="neutral"
                  fullWidth
                  icon={<GoogleIcon className="w-5 h-5" />}
                  onClick={handleGoogleRegister}
                  className="justify-center"
                >
                  Kontynuuj z Google
                </Button>
              </div>
              <Divider label="lub zarejestruj się emailem" className="my-6" />
            </>
          )}

          <RegisterForm selectedRole={role} showRoleSelection={false} />
        </Card>

        <p className="text-primary-light-300 text-center">
          Masz już konto?{' '}
          <Link
            to={loginTarget}
            state={from ? { from } : undefined}
            className="text-ratuj-300 hover:text-ratuj-200 font-medium focus-outline rounded"
          >
            Zaloguj się
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
